import * as React from "react";
import * as xo from "@attraqt/xo-js";

import {
  useAnalyticsConfiguration,
  useLocalizationProvider,
} from "../../providers";

import { ACountry } from "../../model";

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;
/**
 * XO For some extremely strange reason XO doesn't export its types properly,
 * so we have to manually extract them ourselves.
 */
export type ActivityType = ArrayElement<Parameters<typeof xo.activity.send>[0]>;

interface UseXOConsumerInterface {
  pushToTheDataLayer: (event: ActivityType) => void;
  consumerInitializationGuard: <AnalyticsInterface extends unknown>(props: {
    initializedInterface: AnalyticsInterface;
  }) => AnalyticsInterface;
}

export const useXO = () => {
  const { isConfigurationLoaded, analyticsConfiguration } =
    useAnalyticsConfiguration();
  const {
    sessionId,
    ecommerceId,
    xo: xoConfiguration,
  } = analyticsConfiguration || {};
  const { enabled, token } = xoConfiguration || {};

  const { countryCode } = useLocalizationProvider();

  const [isInitialized, setIsInitialized] = React.useState(false);

  const locale = React.useMemo(() => {
    switch (countryCode) {
      case ACountry.Australia:
        return "en_AU";
      case ACountry.NewZealand:
        return "en_NZ";
      case ACountry.Singapore:
        return "en_SG";
      default:
        return "en_AU";
    }
  }, [countryCode]);

  React.useEffect(() => {
    if (!isInitialized) {
      if (isConfigurationLoaded) {
        if (enabled && token) {
          xo.init({
            activity: { trackerKey: token },
          });
          setIsInitialized(true);
        }
      }
    }
  }, [isInitialized, isConfigurationLoaded, enabled, token]);

  const fingerprint = React.useMemo(() => {
    let partialActivity: Partial<ActivityType> = {
      user: {
        identities: {},
        segments: [],
        traits: {},
      },
    };

    if (sessionId) {
      partialActivity.user!.identities.sessionId = sessionId;
    }
    if (ecommerceId) {
      partialActivity.user!.identities.ecommerceId = ecommerceId;
    }

    return partialActivity;
  }, [sessionId, ecommerceId]);
  const pushToTheDataLayer = React.useCallback(
    (event: ActivityType) => {
      event = {
        ...event,
        ...fingerprint,
        metadata: {
          ...(event.metadata || {}),
          locale,
        },
      };
      xo.activity.send(event);
    },
    [locale, fingerprint]
  );

  const consumerInitializationGuard: UseXOConsumerInterface["consumerInitializationGuard"] =
    React.useCallback(
      <AnalyticsInterface extends unknown>(props: {
        initializedInterface: AnalyticsInterface;
      }): AnalyticsInterface => {
        return React.useMemo(
          () =>
            ({
              ...(isInitialized
                ? (props.initializedInterface as { [key: string]: any })
                : {}),
            } as AnalyticsInterface),
          [
            isInitialized,
            fingerprint,
            pushToTheDataLayer,
            props.initializedInterface,
          ]
        );
      },
      [isInitialized, fingerprint, pushToTheDataLayer]
    );

  const value: UseXOConsumerInterface = React.useMemo(
    () => ({
      pushToTheDataLayer,
      consumerInitializationGuard,
    }),
    [pushToTheDataLayer, consumerInitializationGuard]
  );
  return value;
};
